import React, { useState, FormEvent, useEffect, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Stack, Paper, Container } from '@mui/material'
import ProgressButton from '../../Utils/ProgressButton'
import { entityTextField } from '../../Utils/EntityField'
import { AppState } from '../../../reducers/index'
import { CountryDetailState } from '../../../reducers/country/countryDetail'
import { upsertCountry } from '../../../actions/country/countryUpsert'
import Country from '../../../models/Country'
import { useTranslation } from 'react-i18next'
import { ISTableTitle } from '../../Utils/ISTable'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { fetchCountry } from '../../../actions/country/countryDetail'
import { BackButton } from '../../Utils/BackButton'
import Selector from '../../Selector'
import { isNil } from 'ramda'

interface Props {
  country: CountryDetailState
}

const empty: Country = {
  id: null,
  intlCode: '',
  description: '',
  language: '',
  currentPriceListId: 0,
  surplusPrepaymentCode: '',
  surplusPrepaymentQty: 0,
  active: true
}

const CountryUpsert: React.FC<Props> = ({ country }) => {
  const [changed, setChanged] = useState<boolean>(false)
  const [countryInfo, setCountryInfo] = useState<Country>(country.country || empty)
  const { t } = useTranslation('others')
  const dispatch = useDispatch()
  const params = useParams<{ countryId: string }>()
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    if (params.countryId) {
      dispatch(fetchCountry(params.countryId))
    }
  }, [dispatch, params.countryId])


  const change = (newCountryInfo: Country) => {
    newCountryInfo.surplusPrepaymentCode = leadingZeros(newCountryInfo.surplusPrepaymentCode)
    setCountryInfo(newCountryInfo)
    setChanged(true)
  }

  const changeCountry = async (event: FormEvent) => {
    event.preventDefault()
    try {
      await dispatch(upsertCountry(countryInfo))
      setChanged(false)
      if (location.state)
        (location.state as any).update = !(location.state as any).update
      history.goBack()
    }
    catch {
    }
  }

  useEffect(() => {
    if (country.country) {
      setCountryInfo(params.countryId ? country.country : empty)
    }
  }, [country.country, params.countryId])

  const CountryInfoField = useMemo(() => entityTextField(change), 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [])

  const leadingZeros = (data: string) => `0000000${data}`.slice(-7)

  return (
    <Container>
      <Paper style={{ borderRadius: "10px", margin: "10px" }}>
        <ISTableTitle title={t('countries')} />
        <Paper style={{ padding: "10px" }}>
          <form onSubmit={changeCountry}>
            <Stack display="flex" flexDirection="row" flexGrow={1} >
              <CountryInfoField
                style={{ width: "250px", marginRight: "10px" }}
                key="intlCode"
                variant="outlined"
                label={t("intlCode")}
                entity={countryInfo}
                attr="intlCode"
                size="medium"
              />
              <CountryInfoField
                //style={{ width: "100%"}}
                key="description"
                variant="outlined"
                label={t("description")}
                entity={countryInfo}
                attr="description"
                size="medium"
              />
            </Stack>
            <Stack display="flex" flexDirection="row" flexGrow={1} >
              <CountryInfoField
                style={{ marginRight: "10px" }}
                key="language"
                variant="outlined"
                label={t("language")}
                entity={countryInfo}
                attr='language'
                size="medium"
              />
              <CountryInfoField
                key="currentPriceListId"
                variant="outlined"
                label={t("currentPriceListId")}
                entity={countryInfo}
                attr='currentPriceListId'
                type='tel'
                size="medium"
              />
            </Stack>
            <Stack display="flex" flexDirection="row" flexGrow={1} >
              <CountryInfoField
                style={{ marginRight: "10px" }}
                key="surplusPrepaymentCode"
                variant="outlined"
                label={t("surplusPrepaymentCode")}
                entity={countryInfo}
                inputProps={{
                  type: "tel",
                  mask: '9999999',
                  style: {
                    textAlign: 'right',
                    paddingRight: "55px"
                  }
                }}
                //onChange={(event) => countryInfo.surplusPrepaymentCode = leadingZeros(event.target.value)}
                attr="surplusPrepaymentCode"
                size="medium"
              />
              <CountryInfoField
                style={{ marginRight: "10px" }}
                key="surplusPrepaymentQty"
                variant="outlined"
                label={t("surplusPrepaymentQty")}
                entity={countryInfo}
                attr="surplusPrepaymentQty"
                type='number'
                size="medium"
              />
              <Selector
                key="active"
                label={t("active")}
                style={{ width: "200px" }}
                value={isNil(countryInfo.active) ? "" : Boolean(countryInfo.active)}
                textAdapter={(_value) => Boolean(_value) ? t("yes") : t("no")}
                menuItems={[true, false]}
              />
            </Stack>
            <Stack display="flex" flexDirection='row' justifyContent="space-between">
              <BackButton />
              <ProgressButton state={country} disabled={!changed}>
                {t('save')}
              </ProgressButton>
            </Stack>
          </form>
        </Paper>
      </Paper>
    </Container>

  )
}

const mapStateToProps = (state: AppState) => ({
  country: state.countryDetail
})

export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(CountryUpsert)
